import MultiElementSelector from './MultiElementSelector';
import { Dispatch, SetStateAction } from 'react';
import { GoiGroupDto } from '@/generated/model';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '../TargetNameAndDescription';
import { useTranslation } from 'react-i18next';
import { countGoiGroupsExtended, getGoiGroups } from '@/generated/client/goi/goi';
import { Box } from '@mui/material';

export default function MultiGoiGroupSelector({
  selectedGoiGroups,
  setSelectedGoiGroups,
  searchText,
  setSearchText,
  maxElementNumber,
}: {
  selectedGoiGroups: GoiGroupDto[];
  setSelectedGoiGroups: Dispatch<SetStateAction<GoiGroupDto[]>>;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  maxElementNumber: number;
}) {
  async function fetchData(
    page: number,
    pageSize: number,
    pagesToLoad: number,
    filter: string | undefined,
    order: string
  ) {
    const groups = await getGoiGroups({
      offset: page * pageSize,
      limit: pageSize * pagesToLoad,
      filter,
      order,
    });
    return groups.data;
  }
  const { t } = useTranslation();

  async function getRowCount(filter: string | undefined, ids: number[]) {
    const localFilter = filter ? filter : '';
    const count = await countGoiGroupsExtended({ filter: localFilter, ids });
    return count.data;
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => {
        const group = params.row;
        const checked = selectedGoiGroups
          ? selectedGoiGroups.find((selectedGroup) => selectedGroup.id === group.id) !== undefined
          : false;
        return (
          <div className="target-cell" data-qa-element-id="goi-group_icon">
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: `rgb(${group.color.red}, ${group.color.green}, ${group.color.blue})`,
                marginRight: '10px',
                boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
              }}
            />
            <TargetNameAndDescription
              targetName={group.name}
              targetDescription={undefined}
              styleClassName={targetNameAndDescriptionStyle(checked, true, false)}
            />
          </div>
        );
      },
    },
  ];

  const translation = {
    placeHolder: t('filter.goiGroupSearchPlaceholder'),
    tooManySelected: t('error.api.too_many_goigroup_selected', { limit: maxElementNumber }),
  };

  return (
    <MultiElementSelector<GoiGroupDto>
      selectedElements={selectedGoiGroups}
      setSelectedElements={setSelectedGoiGroups}
      searchText={searchText}
      setSearchText={setSearchText}
      maxElementNumber={maxElementNumber}
      fetchData={fetchData}
      getRowCount={getRowCount}
      columns={columns}
      translation={translation}
    />
  );
}
